import React from 'react';
import Box from '@mui/material/Box';
import {
  COLOR_INFO_TEXT,
  COLOR_INFO_BANNER_BG,
  COLOR_INFO_BANNER_BORDER,
  COLOR_DANGER_TEXT,
  COLOR_DANGER_BG,
  COLOR_DANGER,
} from 'theme/colors';
import { GRAY_500 } from 'theme/grayScale';
import {
  CasDuplicateVerifiedErrorBody,
  LastManufacturerRefusalErrorBody,
  ManufacturerNameAction,
  ManufacturerNameOrphanErrorBody,
  ManufacturerRoleNameRemovalRefusedErrorBody,
} from '../services/api/domains/products/types';
import {
  ManufacturerDeleteOrphanErrorBody,
  ManufacturerProductsRemovalOrphanErrorBody,
  ProductNameAction,
} from '../services/api/domains/manufacturers/types';
import { ApiError } from '../services/api/types';

/** Seq 9.2 (PIV-020): the steady-state manufacturer-name gate (Seq 9.1)
 * reports what it did -- or refuses to save at all (Q29) -- via extra
 * response fields on the existing PUT /products/:id and PUT
 * /manufacturers/:id endpoints. This is the one shared presentational
 * banner both the product modal (products.js keys by manufacturerId) and
 * the manufacturer modal (manufacturers.js keys by productId) render it
 * through, rather than two near-identical copies. */

/** Never shown in practice -- the backend always resolves a traceable
 * `Manufacturer #<id>`/`Product #<id>` fallback when the real display name
 * is unavailable (api/sql/manufacturerNameReconciliation.js's
 * `resolveActionDisplayNames`), so a bare id is never what the user sees.
 * Kept only as a last-resort guard against a malformed/incomplete response
 * (code review finding). */
const UNNAMED_PRODUCT = 'an unnamed product';
const UNNAMED_MANUFACTURER = 'an unnamed manufacturer';

/** Branches on `newProductId` (the authoritative outcome signal the backend
 * already uses for its own new-vs-existing-product logic -- see
 * `manufacturerNameReconciliation.js`'s `commitOutcome` derivation), never
 * on whether a *name* happened to resolve. Branching on name presence was a
 * real bug (code review finding): a CREATE_* outcome with a resolution gap
 * would silently render as "matched to an existing product" -- the wrong
 * outcome -- instead of "used to create a new product" with a fallback
 * name. */
function describeOutcome(
  outcome: 'MOVE' | 'CREATE_VERIFIED' | 'CREATE_UNVERIFIED',
  targetProductId: number | null,
  newProductId: number | null,
  targetProductName: string | null,
  newProductName: string | null
): string {
  if (outcome === 'MOVE') {
    return `moved to product "${targetProductName ?? UNNAMED_PRODUCT}"`;
  }
  const casNote = outcome === 'CREATE_VERIFIED' ? ' (CAS-verified)' : '';
  return newProductId != null
    ? `used to create a new product "${newProductName ?? UNNAMED_PRODUCT}"${casNote}`
    : `matched to an existing product "${targetProductName ?? UNNAMED_PRODUCT}"${casNote}`;
}

/** Builds one human-readable line per redirected manufacturer, for the
 * product modal (`PutProductSuccess.manufacturerNameActions`). */
export function describeManufacturerNameActions(
  actions: ManufacturerNameAction[]
): string[] {
  return actions.map(
    a =>
      `Manufacturer "${a.manufacturerName ?? UNNAMED_MANUFACTURER}"'s name was ${describeOutcome(a.outcome, a.targetProductId, a.newProductId, a.targetProductName, a.newProductName)}.`
  );
}

/** Builds one human-readable line per redirected product, for the
 * manufacturer modal (`ManufacturerResponse.manufacturerNameActions`). */
export function describeProductNameActions(
  actions: ProductNameAction[]
): string[] {
  return actions.map(
    a =>
      `Product "${a.productName ?? UNNAMED_PRODUCT}"'s manufacturer name was ${describeOutcome(a.outcome, a.targetProductId, a.newProductId, a.targetProductName, a.newProductName)}.`
  );
}

/** Type guard + extractor for the Q29 409 refusal, read off a caught
 * `ApiError`'s `response.data` (the shape `sendError`'s `extraFields`
 * produces -- see api/utils/errorHandler.js). */
export function getOrphanRefusal(
  err: unknown
): ManufacturerNameOrphanErrorBody | null {
  const apiError = err as ApiError | undefined;
  const data = apiError?.response?.data as
    Partial<ManufacturerNameOrphanErrorBody> | undefined;
  if (data?.category !== 'manufacturer_name_would_orphan') {
    return null;
  }
  return data as ManufacturerNameOrphanErrorBody;
}

/** Type guard + extractor for the Seq 14.1 (PIV-008) last-manufacturer-
 * removal 409, read off a caught `ApiError`'s `response.data`. Keyed on
 * `code` rather than `category` since this refusal has no corresponding
 * success-path payload to share a discriminant shape with (unlike the Q29
 * orphan refusal above, which mirrors `PutProductSuccess`). */
export function getLastManufacturerRefusal(
  err: unknown
): LastManufacturerRefusalErrorBody | null {
  const apiError = err as ApiError | undefined;
  const data = apiError?.response?.data as
    Partial<LastManufacturerRefusalErrorBody> | undefined;
  if (data?.code !== 'LAST_MANUFACTURER_REMOVAL_REFUSED') {
    return null;
  }
  return data as LastManufacturerRefusalErrorBody;
}

/** Type guard + extractor for the Seq 14.1 (PIV-008) manufacturer-delete
 * orphan 409, read off a caught `ApiError`'s `response.data`. */
export function getManufacturerDeleteOrphanRefusal(
  err: unknown
): ManufacturerDeleteOrphanErrorBody | null {
  const apiError = err as ApiError | undefined;
  const data = apiError?.response?.data as
    Partial<ManufacturerDeleteOrphanErrorBody> | undefined;
  if (data?.code !== 'MANUFACTURER_DELETE_WOULD_ORPHAN_PRODUCTS') {
    return null;
  }
  return data as ManufacturerDeleteOrphanErrorBody;
}

/** Type guard + extractor for the Seq 14.1 (PIV-008) Products-panel removal
 * orphan 409 (editing a manufacturer's own product list down to zero
 * manufacturers for one of them), read off a caught `ApiError`'s
 * `response.data`. */
export function getProductsRemovalOrphanRefusal(
  err: unknown
): ManufacturerProductsRemovalOrphanErrorBody | null {
  const apiError = err as ApiError | undefined;
  const data = apiError?.response?.data as
    Partial<ManufacturerProductsRemovalOrphanErrorBody> | undefined;
  if (data?.code !== 'MANUFACTURER_PRODUCTS_REMOVAL_WOULD_ORPHAN') {
    return null;
  }
  return data as ManufacturerProductsRemovalOrphanErrorBody;
}

/** Type guard + extractor for the tr_ProductNames_ManufacturerRoleInvariant
 * (V1_4_67) trigger-rollback 409 (PR #877 review) -- the backstop for
 * removal paths the pre-checks above don't catch (a kept manufacturer link
 * whose `man_product_name` is cleared to null, or a Products-panel removal
 * that leaves only an unnamed link), read off a caught `ApiError`'s
 * `response.data`. */
export function getManufacturerRoleNameRemovalRefusal(
  err: unknown
): ManufacturerRoleNameRemovalRefusedErrorBody | null {
  const apiError = err as ApiError | undefined;
  const data = apiError?.response?.data as
    Partial<ManufacturerRoleNameRemovalRefusedErrorBody> | undefined;
  if (data?.category !== 'manufacturer_role_name_would_orphan') {
    return null;
  }
  return data as ManufacturerRoleNameRemovalRefusedErrorBody;
}

/** Type guard + extractor for the Seq 13.2 (PIV-018) direct-CAS-edit
 * duplicate refusal (409, PR #877 review round 3) -- a PUT that sends
 * `cas_number`/`is_cas_verified` directly, bypassing registry enrichment,
 * read off a caught `ApiError`'s `response.data`. */
export function getCasDuplicateVerifiedRefusal(
  err: unknown
): CasDuplicateVerifiedErrorBody | null {
  const apiError = err as ApiError | undefined;
  const data = apiError?.response?.data as
    Partial<CasDuplicateVerifiedErrorBody> | undefined;
  if (data?.code !== 'CAS_DUPLICATE_VERIFIED') {
    return null;
  }
  return data as CasDuplicateVerifiedErrorBody;
}

export interface ManufacturerNameGateNoticeProps {
  messages: string[];
  /** 'info' for a moved/created notice after a successful save; 'danger'
   * for the Q29 would-orphan refusal. */
  variant: 'info' | 'danger';
  onDismiss: () => void;
}

const ManufacturerNameGateNotice: React.FC<ManufacturerNameGateNoticeProps> = ({
  messages,
  variant,
  onDismiss,
}) => {
  if (messages.length === 0) {
    return null;
  }
  const palette = variant === 'danger' ? dangerPalette : infoPalette;

  return (
    <div
      style={{ ...styles.banner, ...palette.banner }}
      role={variant === 'danger' ? 'alert' : 'status'}
      data-testid="manufacturer-name-gate-notice">
      <div style={{ ...styles.text, color: palette.text }}>
        {messages.map(message => (
          <div key={message}>{message}</div>
        ))}
      </div>
      <Box
        component="button"
        onClick={onDismiss}
        style={styles.dismissButton}
        sx={{
          // Only the pseudo-states plain inline styles can't express live
          // here (copilot-instructions.md's inline-styles/sx exception) --
          // every static value above is a plain `style` object instead
          // (code review fix, Seq 9.2).
          '&:hover': {
            color: palette.text,
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&:active': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
          },
          '&:focus-visible': {
            outline: `2px solid ${palette.text}`,
            outlineOffset: '1px',
          },
        }}
        aria-label="Dismiss notice"
        title="Dismiss"
        data-testid="manufacturer-name-gate-notice-close">
        ×
      </Box>
    </div>
  );
};

const infoPalette = {
  banner: {
    backgroundColor: COLOR_INFO_BANNER_BG,
    border: `1px solid ${COLOR_INFO_BANNER_BORDER}`,
  },
  text: COLOR_INFO_TEXT,
};

const dangerPalette = {
  banner: {
    backgroundColor: COLOR_DANGER_BG,
    border: `1px solid ${COLOR_DANGER}`,
  },
  text: COLOR_DANGER_TEXT,
};

const styles: Record<string, React.CSSProperties> = {
  banner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.5rem',
    padding: '0.5rem 0.75rem',
    borderRadius: '0.375rem',
    margin: '0.5rem 0',
    // Q29 refusal keeps the manufacturer/product edit modal open (the
    // success/MOVE path closes it first), and that modal's own overlay is
    // `position: fixed; zIndex: 10000` (modals/styles.ts). Without an
    // explicit position + higher z-index here, this banner -- rendered at
    // the page level, not inside the modal -- painted underneath the
    // modal's backdrop: present in the DOM and announced to screen readers
    // (role="alert"/"status"), but invisible to a sighted user, who saw
    // their save silently fail with no visible explanation. Confirmed live
    // via getComputedStyle() before this fix: zIndex was 'auto'.
    position: 'relative',
    zIndex: 10001,
  },
  text: {
    flex: 1,
    minWidth: 0,
    fontSize: '0.8125rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  dismissButton: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: GRAY_500,
    fontSize: '1.125rem',
    lineHeight: 1,
    padding: '0 0.25rem',
    borderRadius: '0.25rem',
  },
};

export default ManufacturerNameGateNotice;
